import React from 'react';
import { CreditCard, Pencil, Plus } from 'lucide-react';
import { SETTINGS_BTN_DANGER_OUTLINE, SETTINGS_BTN_OUTLINE } from '../../settings/utils/settingsSectionStyles';
import type { VehicleDetail, VehicleFuelTransaction } from '../types';
import { formatCents, formatFuelCardLabel, formatIsoDate, formatLiters } from '../format';
import { EmptyState, FUHRPARK_BTN_GREEN, FUHRPARK_BTN_GHOST } from './fuhrparkUi';

const KPI_LABEL = 'text-xs font-medium text-app-text-subtle';

function monthLabel(ymd: string): string {
  const [y, m] = ymd.split('-');
  if (!y || !m) return '';
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function isImported(tx: VehicleFuelTransaction): boolean {
  return tx.source === 'dkv';
}

function SourceChip({ tx }: { tx: VehicleFuelTransaction }) {
  if (!isImported(tx)) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-app-text-subtle" title="Automatisch aus DKV importiert">
      <span className="h-1.5 w-1.5 rounded-full bg-[#f28c00]" aria-hidden />
      DKV
    </span>
  );
}

export function FuelTab({
  fuel,
  canEdit,
  onAddTx,
  onEditTx,
  onDeleteTx,
  onEditCard,
  onDeleteCard,
}: {
  fuel: VehicleDetail['fuel'];
  canEdit: boolean;
  onAddTx: () => void;
  onEditTx: (tx: VehicleFuelTransaction) => void;
  onDeleteTx: (id: string) => void;
  onEditCard: () => void;
  onDeleteCard: () => void;
}) {
  const card = fuel?.card ?? null;
  const transactions = fuel?.transactions ?? [];
  const usedCents = fuel?.used_cents ?? 0;
  const limitCents = card?.monthly_limit_cents ?? 0;
  const month = fuel?.month_from ? monthLabel(fuel.month_from) : '';
  const limitPercent = limitCents > 0 ? Math.min(100, Math.round((usedCents / limitCents) * 100)) : null;
  const importedCount = transactions.filter(isImported).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-app-border bg-app-surface p-3.5">
          <p className={KPI_LABEL}>Tankkarte</p>
          <p className="mt-1.5 text-base font-semibold text-app-text">
            {card ? formatFuelCardLabel(card.provider, card.last_four) : 'Keine Karte'}
          </p>
          <p className="mt-0.5 text-xs text-app-text-subtle">
            {!card
              ? 'Noch nicht zugewiesen'
              : card.external_card_number
                ? `Mit DKV verknüpft${card.valid_until ? ` · gültig bis ${formatIsoDate(card.valid_until)}` : ''}`
                : card.provider === 'dkv'
                  ? 'Noch nicht mit DKV verknüpft – Kennzeichen muss zum DKV-Eintrag passen'
                  : card.valid_until
                    ? `Gültig bis ${formatIsoDate(card.valid_until)}`
                    : 'Ohne Ablaufdatum'}
          </p>
        </div>
        <div className="rounded-lg border border-app-border bg-app-surface p-3.5">
          <p className={KPI_LABEL}>{month ? `Tankkosten ${month}` : 'Tankkosten diesen Monat'}</p>
          <p className="mt-1.5 text-base font-semibold text-app-text">{formatCents(usedCents)}</p>
          <p className="mt-0.5 text-xs text-app-text-subtle">
            {limitCents > 0
              ? `Limit ${formatCents(limitCents)}${limitPercent != null ? ` · ${limitPercent} %` : ''}`
              : card?.external_card_number
                ? 'Bei DKV kein Monatslimit gesetzt'
                : 'Kein Monatslimit hinterlegt'}
          </p>
        </div>
        <div className="rounded-lg border border-app-border bg-app-surface p-3.5">
          <p className={KPI_LABEL}>Tankvorgänge</p>
          <p className="mt-1.5 text-base font-semibold text-app-text">{transactions.length}</p>
          <p className="mt-0.5 text-xs text-app-text-subtle">
            {importedCount > 0 ? `${importedCount} aus DKV importiert` : 'Manuell erfasst'}
          </p>
        </div>
      </div>

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={FUHRPARK_BTN_GREEN} onClick={onAddTx}>
            <Plus className="h-3.5 w-3.5" />
            Tankvorgang erfassen
          </button>
          <button type="button" className={FUHRPARK_BTN_GHOST} onClick={onEditCard}>
            {card ? <Pencil className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
            {card ? 'Tankkarte ändern' : 'Tankkarte zuweisen'}
          </button>
          {card ? (
            <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} ml-auto`} onClick={onDeleteCard}>
              Karte entfernen
            </button>
          ) : null}
        </div>
      ) : null}

      {transactions.length === 0 ? (
        <EmptyState text="Noch keine Tankvorgänge. DKV-Tankungen erscheinen hier automatisch, sobald die Integration verbunden ist und das Kennzeichen übereinstimmt." />
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {transactions.map((tx) => (
              <article key={tx.id} className="rounded-lg border border-app-border bg-app-surface px-3.5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-app-text">{tx.station || 'Tankstelle unbekannt'}</p>
                    <p className="text-xs text-app-text-subtle">
                      {formatIsoDate(tx.occurred_on)}
                      {tx.liters_milli != null ? ` · ${formatLiters(tx.liters_milli)}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <p className="text-[13px] font-semibold text-app-text">{formatCents(tx.amount_cents)}</p>
                    <SourceChip tx={tx} />
                  </div>
                </div>
                {canEdit && !isImported(tx) ? (
                  <div className="mt-2 flex gap-2">
                    <button type="button" className={`${SETTINGS_BTN_OUTLINE} !h-8 text-xs`} onClick={() => onEditTx(tx)}>
                      Bearbeiten
                    </button>
                    <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} !h-8 text-xs`} onClick={() => onDeleteTx(tx.id)}>
                      Löschen
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>

          <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[16%]" />
                <col className="w-[36%]" />
                <col className="w-[14%]" />
                <col className="w-[16%]" />
                <col className="w-[18%]" />
              </colgroup>
              <thead className="bg-app-surface-muted text-left">
                <tr className="text-xs font-medium text-app-text-subtle">
                  <th className="px-4 py-2.5">Datum</th>
                  <th className="px-4 py-2.5">Tankstelle</th>
                  <th className="px-4 py-2.5 text-right">Menge</th>
                  <th className="px-4 py-2.5 text-right">Betrag</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const editable = canEdit && !isImported(tx);
                  return (
                    <tr
                      key={tx.id}
                      className={`border-t border-app-border ${editable ? 'cursor-pointer hover:bg-app-hover' : ''}`}
                      onClick={() => editable && onEditTx(tx)}
                    >
                      <td className="px-4 py-3 text-app-text-muted">{formatIsoDate(tx.occurred_on)}</td>
                      <td className="min-w-0 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium text-app-text">{tx.station || '—'}</span>
                          <SourceChip tx={tx} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-app-text-muted">{formatLiters(tx.liters_milli)}</td>
                      <td className="px-4 py-3 text-right font-medium text-app-text">{formatCents(tx.amount_cents)}</td>
                      <td className="px-4 py-3 text-right">
                        {editable ? (
                          <button
                            type="button"
                            className="text-sm text-rose-600 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteTx(tx.id);
                            }}
                          >
                            Löschen
                          </button>
                        ) : isImported(tx) ? (
                          <span className="text-xs text-app-text-subtle">Import</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
